import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Brain, CloudCog, FileText, Save, Loader2, Check, RotateCcw, Eye, EyeOff, RefreshCw, Search, Briefcase } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage({ user }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [evalSystem, setEvalSystem] = useState("");
  const [evalTemplate, setEvalTemplate] = useState("");
  const [resumePrompt, setResumePrompt] = useState("");
  const [ghClientId, setGhClientId] = useState("");
  const [ghClientSecret, setGhClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [excludedStatuses, setExcludedStatuses] = useState(["hired", "rejected"]);
  const [excludedStages, setExcludedStages] = useState(["offer"]);
  const [newExcludedStage, setNewExcludedStage] = useState("");
  const [dirty, setDirty] = useState(false);

  // Greenhouse roles from API
  const [ghRoles, setGhRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [roleSearch, setRoleSearch] = useState("");

  // Jobs
  const [jobs, setJobs] = useState([]);
  const [pollingJob, setPollingJob] = useState(null);

  const fetchSettings = async () => {
    try {
      const resp = await api.get("/settings");
      setSettings(resp.data);
      setEvalSystem(resp.data.eval_system_prompt || "");
      setEvalTemplate(resp.data.eval_prompt_template || "");
      setResumePrompt(resp.data.resume_parse_prompt || "");
      setGhClientId(resp.data.greenhouse_client_id || "");
      setGhClientSecret(resp.data.greenhouse_client_secret || "");
      setAllowedRoles(resp.data.greenhouse_allowed_roles || []);
      setExcludedStatuses(resp.data.greenhouse_excluded_statuses || ["hired", "rejected"]);
      setExcludedStages(resp.data.greenhouse_excluded_stages || ["offer"]);
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchGhRoles = async () => {
    setLoadingRoles(true);
    try {
      const resp = await api.get("/greenhouse/roles");
      setGhRoles(resp.data.roles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch Greenhouse roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => { fetchSettings(); fetchJobs(); }, []);

  // Poll active jobs
  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === "running" || j.status === "pending");
    if (hasActive) {
      const interval = setInterval(fetchJobs, 3000);
      return () => clearInterval(interval);
    }
  }, [jobs]);

  const fetchJobs = async () => {
    try {
      const resp = await api.get("/jobs/recent");
      setJobs(resp.data);
    } catch (e) {}
  };

  const startJob = async (endpoint, body = {}) => {
    try {
      const resp = await api.post(endpoint, body);
      toast.success(resp.data.message);
      fetchJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start job");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        eval_system_prompt: evalSystem,
        eval_prompt_template: evalTemplate,
        resume_parse_prompt: resumePrompt,
        greenhouse_client_id: ghClientId,
        greenhouse_allowed_roles: allowedRoles,
        greenhouse_excluded_statuses: excludedStatuses,
        greenhouse_excluded_stages: excludedStages,
      };
      if (ghClientSecret && ghClientSecret !== "***") {
        payload.greenhouse_client_secret = ghClientSecret;
      }
      await api.put("/settings", payload);
      toast.success("Settings saved");
      setDirty(false);
      fetchSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const markDirty = (setter) => (val) => { setter(val); setDirty(true); };

  const toggleRole = (role) => {
    setDirty(true);
    setAllowedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const selectAll = () => { setDirty(true); setAllowedRoles([...ghRoles]); };
  const deselectAll = () => { setDirty(true); setAllowedRoles([]); };

  const filteredGhRoles = ghRoles.filter((r) =>
    !roleSearch || r.toLowerCase().includes(roleSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const templateVars = [
    "{candidate_name}", "{role_title}", "{department}", "{resume_summary}",
    "{skills}", "{experience_years}", "{education}", "{work_history}",
    "{current_role}", "{current_company}", "{certifications}",
    "{highlights}", "{competencies}", "{review_count}", "{reviewer_feedback}"
  ];

  return (
    <div className="px-4 py-6 sm:p-6 md:p-8 lg:p-12 space-y-6 animate-fade-in" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white uppercase">Settings</h1>
          <p className="text-zinc-500 mt-1 text-sm">Configure AI prompts and integrations</p>
        </div>
        <Button
          data-testid="save-settings-btn"
          onClick={handleSave}
          disabled={saving || !dirty}
          className={`${dirty ? "bg-blue-600 hover:bg-blue-700" : "bg-zinc-700"} text-white`}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {dirty ? "Save Changes" : "Saved"}
        </Button>
      </div>

      <Tabs defaultValue="jobs" className="space-y-4">
        <TabsList className="bg-zinc-900 border border-white/10 p-1 flex-wrap h-auto gap-1">
          <TabsTrigger value="jobs" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-jobs">
            <Loader2 className="w-4 h-4 mr-1.5" /> Processing
          </TabsTrigger>
          <TabsTrigger value="roles" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-roles">
            <Briefcase className="w-4 h-4 mr-1.5" /> Sync Roles
          </TabsTrigger>
          <TabsTrigger value="evaluation" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-evaluation">
            <Brain className="w-4 h-4 mr-1.5" /> AI Evaluation
          </TabsTrigger>
          <TabsTrigger value="parsing" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-parsing">
            <FileText className="w-4 h-4 mr-1.5" /> Resume Parsing
          </TabsTrigger>
          <TabsTrigger value="greenhouse" className="data-[state=active]:bg-zinc-800 text-xs sm:text-sm" data-testid="tab-greenhouse">
            <CloudCog className="w-4 h-4 mr-1.5" /> Credentials
          </TabsTrigger>
        </TabsList>

        {/* Processing / Jobs Tab */}
        <TabsContent value="jobs" className="space-y-4">
          {/* Action Buttons */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">
                Batch Operations
              </CardTitle>
              <p className="text-xs text-zinc-500 mt-1">Run background jobs across all candidates. Progress persists across pods.</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Button
                  data-testid="job-parse-all-btn"
                  onClick={() => startJob("/jobs/parse-all")}
                  disabled={jobs.some((j) => j.type === "parse" && (j.status === "running" || j.status === "pending"))}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-auto py-3"
                >
                  <div className="text-center">
                    <FileText className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Parse All CVs</span>
                    <span className="text-[10px] block text-blue-200/60 mt-0.5">Unparsed resumes only</span>
                  </div>
                </Button>
                <Button
                  data-testid="job-evaluate-btn"
                  onClick={() => startJob("/jobs/evaluate-all", {})}
                  disabled={jobs.some((j) => j.type === "evaluate" && (j.status === "running" || j.status === "pending"))}
                  className="bg-violet-600 hover:bg-violet-700 text-white h-auto py-3"
                >
                  <div className="text-center">
                    <Brain className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Evaluate All</span>
                    <span className="text-[10px] block text-violet-200/60 mt-0.5">Unevaluated candidates</span>
                  </div>
                </Button>
                <Button
                  data-testid="job-reeval-btn"
                  onClick={() => startJob("/jobs/evaluate-all", { re_evaluate: true })}
                  disabled={jobs.some((j) => j.type === "evaluate" && (j.status === "running" || j.status === "pending"))}
                  className="bg-amber-600 hover:bg-amber-700 text-white h-auto py-3"
                >
                  <div className="text-center">
                    <Brain className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-sm font-medium">Re-evaluate All</span>
                    <span className="text-[10px] block text-amber-200/60 mt-0.5">All parsed candidates</span>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Active & Recent Jobs */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">
                  Job History
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchJobs} className="text-zinc-500 hover:text-white text-xs">
                  <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2 space-y-3">
              {jobs.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-6">No jobs run yet</p>
              ) : (
                jobs.map((job) => {
                  const pct = job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
                  const isActive = job.status === "running" || job.status === "pending";
                  return (
                    <div key={job.job_id} className={`p-4 rounded-xl border ${isActive ? "border-blue-500/30 bg-blue-500/5" : "border-white/5 bg-zinc-800/30"}`} data-testid={`job-${job.job_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {job.type === "parse" ? <FileText className="w-4 h-4 text-blue-400" /> : <Brain className="w-4 h-4 text-violet-400" />}
                          <span className="text-sm font-medium text-zinc-200">{job.description}</span>
                        </div>
                        <Badge className={`text-[10px] ${
                          job.status === "running" ? "bg-blue-500/15 text-blue-400 border-blue-500/30" :
                          job.status === "completed" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                          job.status === "failed" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                          job.status === "stalled" ? "bg-amber-500/15 text-amber-400 border-amber-500/30" :
                          "bg-zinc-700 text-zinc-400 border-white/10"
                        }`}>
                          {job.status === "running" && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                          {job.status}
                        </Badge>
                      </div>
                      
                      {job.total > 0 && (
                        <>
                          <div className="w-full h-2 rounded-full bg-zinc-800 overflow-hidden mb-1.5">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                job.status === "failed" ? "bg-red-500" :
                                job.status === "completed" ? "bg-emerald-500" : "bg-blue-500"
                              }`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between text-xs text-zinc-500">
                            <span className="font-mono">{job.processed}/{job.total} ({pct}%)</span>
                            <span>{job.failed > 0 && <span className="text-red-400">{job.failed} failed</span>}</span>
                          </div>
                        </>
                      )}
                      
                      {isActive && job.current_item && (
                        <p className="text-xs text-zinc-500 mt-1.5 truncate">Processing: <span className="text-zinc-300">{job.current_item}</span></p>
                      )}
                      
                      {job.error && (
                        <p className="text-xs text-red-400 mt-1.5">{job.error}</p>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">
                    Greenhouse Roles to Sync
                  </CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">
                    Select which roles to pull candidates from. Only checked roles will be imported during sync.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-mono text-xs">
                    {allowedRoles.length} selected
                  </Badge>
                  <Button
                    data-testid="fetch-gh-roles-btn"
                    variant="outline"
                    size="sm"
                    onClick={fetchGhRoles}
                    disabled={loadingRoles}
                    className="border-white/10 text-zinc-400 hover:text-white text-xs"
                  >
                    {loadingRoles ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                    Fetch from Greenhouse
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2 space-y-3">
              {ghRoles.length > 0 ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                      <Input
                        data-testid="role-search"
                        value={roleSearch}
                        onChange={(e) => setRoleSearch(e.target.value)}
                        placeholder="Search roles..."
                        className="pl-9 bg-zinc-800 border-white/10 text-zinc-200 text-sm h-9"
                      />
                    </div>
                    <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs text-blue-400 hover:text-blue-300">Select All</Button>
                    <Button variant="ghost" size="sm" onClick={deselectAll} className="text-xs text-zinc-500 hover:text-zinc-300">Clear</Button>
                  </div>
                  <ScrollArea className="h-[400px] pr-2">
                    <div className="space-y-1">
                      {filteredGhRoles.map((role) => {
                        const checked = allowedRoles.includes(role);
                        return (
                          <label
                            key={role}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                              checked ? "bg-blue-500/10 border border-blue-500/20" : "bg-zinc-800/30 border border-transparent hover:bg-zinc-800/60"
                            }`}
                            data-testid={`role-checkbox-${role.trim().replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleRole(role)}
                              className="border-zinc-600 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                            />
                            <span className={`text-sm ${checked ? "text-white font-medium" : "text-zinc-400"}`}>{role}</span>
                            {checked && <Check className="w-3.5 h-3.5 text-blue-400 ml-auto" />}
                          </label>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </>
              ) : (
                <div className="text-center py-10 space-y-3">
                  {allowedRoles.length > 0 ? (
                    <>
                      <p className="text-sm text-zinc-400">Currently syncing {allowedRoles.length} roles:</p>
                      <div className="flex flex-wrap gap-1.5 justify-center max-w-2xl mx-auto">
                        {allowedRoles.map((r) => (
                          <Badge key={r} variant="outline" className="text-xs text-blue-400 border-blue-500/20">{r}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-zinc-600 mt-4">Click "Fetch from Greenhouse" to see all available roles and modify the selection</p>
                    </>
                  ) : (
                    <>
                      <Briefcase className="w-10 h-10 text-zinc-600 mx-auto" />
                      <p className="text-sm text-zinc-400">No roles configured. Click "Fetch from Greenhouse" to load available roles.</p>
                      <p className="text-xs text-zinc-600">If no roles are selected, all roles will be synced.</p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Evaluation Tab */}
        <TabsContent value="evaluation" className="space-y-4">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">System Prompt</CardTitle>
              <p className="text-xs text-zinc-500 mt-1">Sets the AI's persona and behavior for candidate evaluation</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2">
              <Textarea
                data-testid="eval-system-prompt"
                value={evalSystem}
                onChange={(e) => markDirty(setEvalSystem)(e.target.value)}
                className="bg-zinc-800 border-white/10 text-zinc-200 font-mono text-sm min-h-[80px]"
              />
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">Evaluation Prompt Template</CardTitle>
                  <p className="text-xs text-zinc-500 mt-1">Template used to evaluate each candidate. Use variables below.</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setEvalTemplate(settings?.eval_prompt_template || ""); setDirty(false); }} className="text-zinc-500 hover:text-white text-xs">
                  <RotateCcw className="w-3 h-3 mr-1" /> Reset
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2 space-y-3">
              <div className="flex flex-wrap gap-1.5">
                {templateVars.map((v) => (
                  <button key={v} onClick={() => markDirty(setEvalTemplate)(evalTemplate + v)} className="px-2 py-0.5 text-[11px] font-mono rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors">{v}</button>
                ))}
              </div>
              <Textarea
                data-testid="eval-prompt-template"
                value={evalTemplate}
                onChange={(e) => markDirty(setEvalTemplate)(e.target.value)}
                className="bg-zinc-800 border-white/10 text-zinc-200 font-mono text-sm min-h-[350px] lg:min-h-[400px]"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resume Parsing Tab */}
        <TabsContent value="parsing" className="space-y-4">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">Resume Parse Prompt</CardTitle>
              <p className="text-xs text-zinc-500 mt-1">Sent to Gemini Flash when parsing PDF resumes. Must request JSON output.</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2">
              <Textarea
                data-testid="resume-parse-prompt"
                value={resumePrompt}
                onChange={(e) => markDirty(setResumePrompt)(e.target.value)}
                className="bg-zinc-800 border-white/10 text-zinc-200 font-mono text-sm min-h-[250px]"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Greenhouse Credentials Tab */}
        <TabsContent value="greenhouse" className="space-y-4">
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight flex items-center gap-2">
                Greenhouse API Credentials
                {settings?.greenhouse_configured && (
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]"><Check className="w-3 h-3 mr-1" /> Connected</Badge>
                )}
              </CardTitle>
              <p className="text-xs text-zinc-500 mt-1">Harvest v3 OAuth credentials. Stored securely.</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2 space-y-4">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Client ID</label>
                <Input data-testid="gh-settings-client-id" value={ghClientId} onChange={(e) => markDirty(setGhClientId)(e.target.value)} placeholder="Your Harvest OAuth Client ID" className="bg-zinc-800 border-white/10 text-zinc-200 font-mono text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1.5 block">Client Secret</label>
                <div className="relative">
                  <Input data-testid="gh-settings-client-secret" type={showSecret ? "text" : "password"} value={ghClientSecret} onChange={(e) => markDirty(setGhClientSecret)(e.target.value)} placeholder="Your Harvest OAuth Client Secret" className="bg-zinc-800 border-white/10 text-zinc-200 font-mono text-sm pr-10" />
                  <button onClick={() => setShowSecret(!showSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-zinc-600 mt-1">Found in Greenhouse: Configure &gt; Dev Center &gt; API Credential Management</p>
              </div>
            </CardContent>
          </Card>

          {/* Exclusion Filters */}
          <Card className="bg-zinc-900/50 border-white/10">
            <CardHeader className="p-4 lg:p-6 pb-2">
              <CardTitle className="font-heading text-base lg:text-lg font-semibold text-white uppercase tracking-tight">
                Sync Exclusion Filters
              </CardTitle>
              <p className="text-xs text-zinc-500 mt-1">Candidates matching these will be skipped during sync</p>
            </CardHeader>
            <CardContent className="p-4 lg:p-6 pt-2 space-y-4">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Excluded Application Statuses</label>
                <div className="flex flex-wrap gap-2">
                  {["hired", "rejected"].map((status) => {
                    const active = excludedStatuses.includes(status);
                    return (
                      <button
                        key={status}
                        data-testid={`exclude-status-${status}`}
                        onClick={() => {
                          setDirty(true);
                          setExcludedStatuses((prev) => active ? prev.filter((s) => s !== status) : [...prev, status]);
                        }}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          active
                            ? "bg-red-500/15 text-red-400 border border-red-500/30"
                            : "bg-zinc-800 text-zinc-500 border border-white/5 hover:text-zinc-300"
                        }`}
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">Excluded Stage Keywords</label>
                <p className="text-xs text-zinc-600 mb-2">Candidates in stages containing these words will be excluded</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {excludedStages.map((stage) => (
                    <span key={stage} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-red-500/10 text-red-400 border border-red-500/20">
                      {stage}
                      <button onClick={() => { setDirty(true); setExcludedStages((prev) => prev.filter((s) => s !== stage)); }} className="hover:text-white">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="new-excluded-stage"
                    value={newExcludedStage}
                    onChange={(e) => setNewExcludedStage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newExcludedStage.trim()) {
                        e.preventDefault();
                        setDirty(true);
                        setExcludedStages((prev) => [...prev, newExcludedStage.trim().toLowerCase()]);
                        setNewExcludedStage("");
                      }
                    }}
                    placeholder="e.g. background check"
                    className="bg-zinc-800 border-white/10 text-zinc-200 text-sm flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (newExcludedStage.trim()) {
                        setDirty(true);
                        setExcludedStages((prev) => [...prev, newExcludedStage.trim().toLowerCase()]);
                        setNewExcludedStage("");
                      }
                    }}
                    className="border-white/10 text-zinc-400"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
